import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/ui/signed-avatar-image';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import { Search } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { formatBusinessDate, formatBusinessTime } from '@/lib/format';
import { driverProfileName, driverProfileOf } from '@/lib/driverProfile';
import { KIND_LABEL, contractInspectionPath, type Grade, type InspectionKind } from '@/lib/inspections';
import { visitResult } from '@/lib/inspectionResult';
import { ResultBadge } from './InspectionChip';
import { PhotoThumb } from './PhotoZoom';

interface Props {
    /** The Board | Records toggle, rendered at the right of the controls row. */
    controlsExtra?: ReactNode;
}

interface RecordRow {
    id: number;
    inspection_at: string | null;
    description: string | null;
    /** NULL: a legacy, notes-only row. */
    kind: InspectionKind | null;
    vehicle_id: number | null;
    contract_id: number | null;
    vehicle_inspection_lines: Array<{ grade: Grade }> | null;
    vehicles: unknown;
    contracts: unknown;
}

export default function InspectionsRecords({ controlsExtra }: Props) {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);

    // When searching, use the RPC to get matching IDs first
    const { data: matchingIds } = useQuery({
        queryKey: ['inspection-search-ids', debouncedSearch],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('search_inspection_ids', { search_term: debouncedSearch });
            if (error) throw error;
            return (data || []) as number[];
        },
        enabled: !!debouncedSearch,
    });

    const { data: countData } = useQuery({
        queryKey: ['vehicle-inspections-count', debouncedSearch, matchingIds],
        queryFn: async () => {
            if (debouncedSearch) {
                return matchingIds?.length || 0;
            }
            const { count, error } = await supabase
                .from('vehicle_inspections')
                .select('*', { count: 'exact', head: true });
            if (error) throw error;
            return count || 0;
        },
        enabled: debouncedSearch ? matchingIds !== undefined : true,
    });

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } = useServerPagination({ totalCount: countData || 0, itemsPerPage: 15 });

    useEffect(() => { resetPage(); }, [debouncedSearch]);

    const { data: inspections, isLoading } = useQuery({
        queryKey: ['vehicle-inspections', currentPage, debouncedSearch, matchingIds],
        queryFn: async () => {
            // The car is read through vehicle_id as well, because a between-contracts visit has no contract.
            let query = supabase
                .from('vehicle_inspections')
                .select(`
                    *,
                    vehicle_inspection_lines(grade),
                    vehicles(
                        make, model, color, updated_at,
                        vehicle_registrations!vehicles_registration_id_fkey(number),
                        vehicle_procurement(listing_url)
                    ),
                    contracts(
                        drivers(
                            profiles(id, admin_id, first_name, last_name, photo)
                        ),
                        vehicles(
                            make, model, color, updated_at,
                            vehicle_registrations(number),
                            vehicle_procurement(listing_url)
                        )
                    )
                `)
                .order('inspection_at', { ascending: false });

            if (debouncedSearch && matchingIds) {
                if (matchingIds.length === 0) return [];
                query = query.in('id', matchingIds);
            }

            query = query.range(from, to);
            const { data, error } = await query;
            if (error) throw error;
            return (data ?? []) as RecordRow[];
        },
        enabled: !!countData && countData > 0,
    });

    /*
     * A VISIT BETWEEN CONTRACTS belongs to no contract, so it has no driver and no contract page of
     * its own. It opens on the car's latest contract's inspection page, which lists every visit on
     * the car for an admin, each marked, and which an admin holding only `inspections` can reach.
     */
    const betweenCars = useMemo(
        () => [...new Set((inspections ?? []).filter((i) => i.kind && !i.contract_id && i.vehicle_id).map((i) => i.vehicle_id as number))].sort((a, b) => a - b),
        [inspections],
    );
    const { data: latestContractOfCar } = useQuery({
        queryKey: ['inspections', 'records-latest-contract', betweenCars],
        queryFn: async () => {
            const { data, error } = await supabase.from('contracts').select('id, vehicle_id').in('vehicle_id', betweenCars).order('id', { ascending: false });
            if (error) throw error;
            const latest: Record<number, number> = {};
            for (const c of data ?? []) if (c.vehicle_id !== null && latest[c.vehicle_id] === undefined) latest[c.vehicle_id] = c.id;
            return latest;
        },
        enabled: betweenCars.length > 0,
    });
    const pageOf = (i: RecordRow): string | null => {
        const contractId = i.contract_id ?? (i.kind && i.vehicle_id ? latestContractOfCar?.[i.vehicle_id] : undefined);
        return contractId ? contractInspectionPath(contractId, i.id) : null;
    };
    const isBetweenContracts = (i: RecordRow) => !!i.kind && !i.contract_id;

    /*
     * THE DRIVER'S OWN PROFILE, NEVER THE FIRST ROW OF THE EMBED. `profiles.driver_id` is unique
     * only WHERE `admin_id IS NULL`, so an admin testing as a driver holds a second row against
     * the same id and position does not say which is which — see `driverProfileOf`. The three
     * readers below all go through that pick, and it is deterministic, so the name, the photo and
     * the initials on a row are always the same person's.
     */
    const getDriverName = (inspection: any) => driverProfileName(inspection.contracts?.drivers?.profiles) ?? 'N/A';

    const getDriverPhoto = (inspection: any) => driverProfileOf(inspection.contracts?.drivers?.profiles)?.photo || null;

    const getDriverInitials = (inspection: any) => {
        const profile = driverProfileOf(inspection.contracts?.drivers?.profiles);
        if (!profile) return '?';
        return `${(profile.first_name || '')[0] || ''}${(profile.last_name || '')[0] || ''}`;
    };

    const getVehicleInfo = (inspection: any) => {
        const vehicle = inspection.vehicles ?? inspection.contracts?.vehicles;
        if (!vehicle) return { regNumber: 'N/A', makeModel: '', color: '#e5e7eb', photoUrl: null };
        const vehicleReg = vehicle.vehicle_registrations;
        const regNumber = Array.isArray(vehicleReg) ? vehicleReg[0]?.number : vehicleReg?.number;
        const listingUrl = vehicle.vehicle_procurement?.listing_url || null;
        const photoUrl = getSbtPhotoUrl(listingUrl, vehicle.updated_at);
        const color = getColorValue(vehicle.color || '');
        return { regNumber: regNumber || 'Unregistered', makeModel: `${vehicle.make} ${vehicle.model}`, color, photoUrl };
    };

    const getSbtPhotoUrl = (listingUrl: string | null, updatedAt: string | null): string | null => {
        if (!listingUrl) return null;
        const match = listingUrl.match(/sbtjapan\.com\/used-cars\/([A-Z0-9]+)/i);
        if (!match) return null;
        const cacheBust = updatedAt ? `?v=${new Date(updatedAt).getTime()}` : '';
        return `https://img.sbtjapan.com/img/carphoto/${match[1]}/normal/f.jpg${cacheBust}`;
    };

    const getColorValue = (color: string): string => {
        const trimmed = color.trim();
        if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) return `#${trimmed}`;
        if (trimmed.startsWith('#')) return trimmed;
        return trimmed || '#e5e7eb';
    };

    // The Ghana day and time, whatever the viewer's zone.
    const formatDateTime = (dateStr: string) => ({
        date: formatBusinessDate(dateStr),
        time: formatBusinessTime(dateStr),
    });

    return (
        <Card className="border-0">
            <CardHeader className="px-0">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative w-full sm:w-96">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input placeholder="Search by reg number, driver, or description..." className="pl-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    {controlsExtra && <div className="ml-auto">{controlsExtra}</div>}
                </div>
            </CardHeader>
            <CardContent className="px-0">
                <ListPagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} />
                {isLoading && countData && countData > 0 ? (
                    <div className="space-y-3">
                        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                    </div>
                ) : inspections && inspections.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Driver</TableHead>
                                <TableHead>Vehicle</TableHead>
                                <TableHead>Kind</TableHead>
                                <TableHead>Result</TableHead>
                                <TableHead>Remarks</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {inspections.map((i) => {
                                const vehicleInfo = getVehicleInfo(i);
                                const dt = i.inspection_at ? formatDateTime(i.inspection_at) : null;
                                const grades = (i.vehicle_inspection_lines ?? []).map((l) => l.grade);
                                const href = pageOf(i);
                                const open = href ? () => navigate(href) : undefined;
                                return (
                                    <TableRow key={i.id} className={open ? 'cursor-pointer' : undefined} onClick={open}>
                                        <TableCell>
                                            {dt ? (
                                                <div>
                                                    {/* The row is a click target; this link is the keyboard's way in. */}
                                                    {href ? (
                                                        <Link to={href} onClick={(e) => e.stopPropagation()} className="font-medium hover:underline">
                                                            {dt.date}
                                                        </Link>
                                                    ) : (
                                                        <p className="font-medium">{dt.date}</p>
                                                    )}
                                                    <p className="text-xs text-muted-foreground">{dt.time}</p>
                                                </div>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {isBetweenContracts(i) ? (
                                                <span className="text-sm text-muted-foreground">Between contracts</span>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <Avatar className="h-8 w-8">
                                                        <SignedAvatarImage src={getDriverPhoto(i) || undefined} alt={getDriverName(i)} />
                                                        <AvatarFallback className="text-[10px] bg-muted">{getDriverInitials(i)}</AvatarFallback>
                                                    </Avatar>
                                                    <span className="font-medium">{getDriverName(i)}</span>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {/* Tap to zoom: a hover card never opens on a phone. */}
                                                {vehicleInfo.photoUrl ? (
                                                    <span onClick={(e) => e.stopPropagation()}>
                                                        <PhotoThumb url={vehicleInfo.photoUrl} alt={vehicleInfo.makeModel || vehicleInfo.regNumber} caption={`${vehicleInfo.regNumber} · ${vehicleInfo.makeModel}`} className="h-8 w-8" />
                                                    </span>
                                                ) : (
                                                    <div className="h-8 w-8 rounded-md border border-gray-200 shrink-0" style={{ backgroundColor: vehicleInfo.color }} />
                                                )}
                                                <div>
                                                    <span className="font-medium">{vehicleInfo.regNumber}</span>
                                                    <p className="text-xs text-muted-foreground">{vehicleInfo.makeModel}</p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm">{i.kind ? KIND_LABEL[i.kind] : '—'}</TableCell>
                                        <TableCell>
                                            <ResultBadge result={visitResult(i.kind, grades)} />
                                        </TableCell>
                                        <TableCell className="max-w-[300px] whitespace-pre-wrap">{i.description || '-'}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">No inspections found</p>
                )}
            </CardContent>
        </Card>
    );
}
